import JsPDF from 'jspdf';
import {
  ucitajFontove,
  ucitajLogo,
} from '../../shared/IzvestajRadnogNaloga/generisiIzvestajPDF';

export const MAKS_FOTOGRAFIJA = 3;
export const MAKS_ZNAKOVA = {
  prethodne: 480,
  naredne: 300,
  vazno: 170,
  opisFotografije: 45,
};

const FONT = 'Roboto';
const SIRINA_STRANE = 210;
const LEVO = 15;
const DESNO = SIRINA_STRANE - LEVO;
const SIRINA = DESNO - LEVO;
const VISINA_REDA_TEKSTA = 4.4;

const BOJE = {
  narandzasta: [240, 102, 10],
  narandzastaSvetla: [255, 244, 234],
  tamna: [17, 17, 17],
  siva: [110, 110, 110],
  linija: [222, 222, 222],
  pozadina: [244, 243, 241],
  traka: [233, 232, 228],
  planirano: [253, 218, 196],
};

const FAZE = {
  pripremaDokumentacije: {
    SRB: 'Priprema dokumentacije',
    ENG: 'Documentation preparation',
  },
  konstruisanje: { SRB: 'Konstruisanje', ENG: 'Design' },
  porucivanjeOpreme: { SRB: 'Poručivanje opreme', ENG: 'Equipment ordering' },
  tehnoloskaPriprema: {
    SRB: 'Tehnološka priprema',
    ENG: 'Technological preparation',
  },
  izradaDokumentacije: {
    SRB: 'Izrada dokumentacije',
    ENG: 'Documentation creation',
  },
  programiranje: { SRB: 'Programiranje', ENG: 'Programming' },
  izradaMasinskihDelova: {
    SRB: 'Izrada mašinskih delova',
    ENG: 'Machining of parts',
  },
  masinskaMontaza: { SRB: 'Mašinska montaža', ENG: 'Mechanical assembly' },
  elektroMontaza: { SRB: 'Elektro montaža', ENG: 'Electrical assembly' },
  testiranje: { SRB: 'Testiranje', ENG: 'Testing' },
  isporukaIPustanjeURad: {
    SRB: 'Isporuka i puštanje u rad',
    ENG: 'Delivery and commissioning',
  },
};

const PREVODI = {
  SRB: {
    naslov: 'DVONEDELJNI IZVEŠTAJ O PROJEKTU',
    projekat: 'Projekat',
    kupac: 'Kupac',
    ukupno: 'UKUPAN PROGRES',
    rok: 'ROK',
    autor: 'IZVEŠTAJ PRIPREMIO',
    faze: 'PROGRES PO FAZAMA',
    prethodne: 'PRETHODNE 2 NEDELJE',
    naredne: 'NAREDNE 2 NEDELJE',
    vazno: 'VAŽNO!',
    fotografije: 'FOTOGRAFIJE',
    izvestaj: 'Izveštaj',
    plan: 'PLAN',
    uradjeno: 'Urađeno',
    planirano: 'Planirano',
    datumIzvestaja: 'Datum izveštaja',
  },
  ENG: {
    naslov: 'BI-WEEKLY PROJECT REPORT',
    projekat: 'Project',
    kupac: 'Customer',
    ukupno: 'OVERALL PROGRESS',
    rok: 'DEADLINE',
    autor: 'REPORT PREPARED BY',
    faze: 'PROGRESS BY PHASE',
    prethodne: 'LAST 2 WEEKS',
    naredne: 'NEXT 2 WEEKS',
    vazno: 'IMPORTANT!',
    fotografije: 'PHOTOS',
    izvestaj: 'Report',
    plan: 'SCHEDULE',
    uradjeno: 'Done',
    planirano: 'Planned',
    datumIzvestaja: 'Report date',
  },
};

const tekst = (vrednost) =>
  vrednost === null || vrednost === undefined ? '' : String(vrednost).trim();

const uDatum = (vrednost) => {
  if (!vrednost) return null;
  const datum = new Date(vrednost);
  return Number.isNaN(datum.getTime()) ? null : datum;
};

export const formatDatum = (vrednost) => {
  const datum = uDatum(vrednost);
  if (!datum) return '—';
  const dan = String(datum.getDate()).padStart(2, '0');
  const mesec = String(datum.getMonth() + 1).padStart(2, '0');
  return `${dan}.${mesec}.${datum.getFullYear()}`;
};

// ISO kalendarska nedelja
const kalendarskaNedelja = (vrednost) => {
  const datum = uDatum(vrednost);
  if (!datum) return '';
  const d = new Date(
    Date.UTC(datum.getFullYear(), datum.getMonth(), datum.getDate()),
  );
  const dan = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dan);
  const pocetak = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - pocetak) / 86400000 + 1) / 7);
};

const formatProcenat = (vrednost) => {
  const broj = Number(vrednost);
  if (!Number.isFinite(broj)) return '0%';
  return `${Math.round(broj)}%`;
};

const sirinaTrake = (vrednost, sirina) => {
  const broj = Number(vrednost);
  if (!Number.isFinite(broj)) return 0;
  return (Math.min(100, Math.max(0, broj)) / 100) * sirina;
};

// Tekst ne sme da pređe predviđeni prostor, jer izveštaj mora da stane na jednu stranu
const lineOgraniceno = (doc, vrednost, sirina, maksRedova) => {
  const redovi = doc.splitTextToSize(tekst(vrednost), sirina);
  if (redovi.length <= maksRedova) return redovi;
  const skraceni = redovi.slice(0, maksRedova);
  skraceni[maksRedova - 1] = `${skraceni[maksRedova - 1].replace(
    /\s*\S*$/,
    '',
  )}…`;
  return skraceni;
};

const naslovSekcije = (doc, naslov, x, y, sirina) => {
  doc.setFont(FONT, 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...BOJE.tamna);
  doc.text(naslov, x, y);
  doc.setDrawColor(...BOJE.linija);
  doc.setLineWidth(0.2);
  doc.line(x, y + 1.8, x + sirina, y + 1.8);
};

const uklopiSliku = (slika, sirina, visina) => {
  const odnos = Math.min(sirina / slika.sirina, visina / slika.visina);
  return { s: slika.sirina * odnos, v: slika.visina * odnos };
};

const dodajZaglavlje = (doc, p, izvestaj, logoInmold) => {
  doc.setFillColor(...BOJE.narandzasta);
  doc.rect(0, 0, SIRINA_STRANE, 3, 'F');

  if (logoInmold) {
    doc.addImage(logoInmold, 'PNG', LEVO, 8, 34, (34 * 227) / 916, 'inmold');
  }

  const nedelja = kalendarskaNedelja(izvestaj.datumIzvestaja);
  doc.setFont(FONT, 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...BOJE.siva);
  doc.text(p.naslov, DESNO, 11, { align: 'right' });
  doc.setFontSize(10);
  doc.setTextColor(...BOJE.tamna);
  doc.text(
    `CW${nedelja} · ${formatDatum(izvestaj.datumIzvestaja)}`,
    DESNO,
    16,
    { align: 'right' },
  );
  doc.setDrawColor(...BOJE.tamna);
  doc.setLineWidth(0.3);
  doc.line(LEVO, 19.5, DESNO, 19.5);

  doc.setFontSize(24);
  doc.setTextColor(...BOJE.tamna);
  const naslov = `${p.projekat} ${tekst(izvestaj.sifraMasine)}`;
  doc.text(naslov, LEVO, 31);
  doc.setTextColor(...BOJE.narandzasta);
  doc.text('.', LEVO + doc.getTextWidth(naslov), 31);

  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...BOJE.siva);
  doc.text(
    lineOgraniceno(doc, `${p.kupac}: ${tekst(izvestaj.nazivKupca)}`, 125, 1),
    LEVO,
    37.5,
  );

  if (izvestaj.logo) {
    const { s, v } = uklopiSliku(izvestaj.logo, 45, 18);
    doc.addImage(
      izvestaj.logo.podaci,
      'JPEG',
      DESNO - s,
      22 + (18 - v) / 2,
      s,
      v,
      'logoKupca',
    );
  }
};

const dodajKartice = (doc, p, izvestaj) => {
  const y = 43;
  const visina = 22;
  const razmak = 4;
  const sirinaKartice = (SIRINA - 2 * razmak) / 3;

  doc.setFillColor(...BOJE.pozadina);
  doc.roundedRect(LEVO, y, sirinaKartice, visina, 2, 2, 'F');
  doc.setFont(FONT, 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...BOJE.siva);
  doc.text(p.ukupno, LEVO + 4, y + 6);
  doc.setFontSize(18);
  doc.setTextColor(...BOJE.tamna);
  doc.text(formatProcenat(izvestaj.ukupanProgres), LEVO + 4, y + 14);
  const sirinaTrakeKartice = sirinaKartice - 8;
  doc.setFillColor(...BOJE.traka);
  doc.roundedRect(LEVO + 4, y + 17, sirinaTrakeKartice, 1.5, 0.7, 0.7, 'F');
  const popunjeno = sirinaTrake(izvestaj.ukupanProgres, sirinaTrakeKartice);
  if (popunjeno > 0) {
    doc.setFillColor(...BOJE.narandzasta);
    doc.roundedRect(LEVO + 4, y + 17, popunjeno, 1.5, 0.7, 0.7, 'F');
  }

  const xRok = LEVO + sirinaKartice + razmak;
  doc.setFillColor(...BOJE.tamna);
  doc.roundedRect(xRok, y, sirinaKartice, visina, 2, 2, 'F');
  doc.setFontSize(7.5);
  doc.setTextColor(200);
  doc.text(p.rok, xRok + 4, y + 6);
  doc.setFontSize(16);
  doc.setTextColor(255);
  doc.text(formatDatum(izvestaj.rok), xRok + 4, y + 14);

  const xAutor = xRok + sirinaKartice + razmak;
  doc.setFillColor(...BOJE.pozadina);
  doc.roundedRect(xAutor, y, sirinaKartice, visina, 2, 2, 'F');
  doc.setFontSize(7.5);
  doc.setTextColor(...BOJE.siva);
  doc.text(p.autor, xAutor + 4, y + 6);
  doc.setFontSize(10);
  doc.setTextColor(...BOJE.tamna);
  doc.text(
    lineOgraniceno(doc, izvestaj.autor, sirinaKartice - 8, 2),
    xAutor + 4,
    y + 12.5,
  );
};

const Y_NASLOVA_FAZA = 73;
const SIRINA_FAZA_UZ_PLAN = 90;
const X_PLANA = LEVO + SIRINA_FAZA_UZ_PLAN + 5;
const SIRINA_NAZIVA_PLANA = 37;
const DAN_MS = 86400000;

const imaPlanFaze = (faza) => {
  const od = uDatum(faza.datumOd);
  const doDatuma = uDatum(faza.datumDo);
  return Boolean(od && doDatuma && od <= doDatuma);
};

const fazeSaPlanom = (izvestaj) => izvestaj.faze.filter(imaPlanFaze);

const ponedeljak = (datum) => {
  const d = new Date(datum.getFullYear(), datum.getMonth(), datum.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

const dodajPlan = (doc, p, jezik, izvestaj, faze) => {
  const izvestajDatum = uDatum(izvestaj.datumIzvestaja);
  const vremena = faze.flatMap((faza) => [
    uDatum(faza.datumOd).getTime(),
    uDatum(faza.datumDo).getTime(),
  ]);
  if (izvestajDatum) vremena.push(izvestajDatum.getTime());

  const pocetak = ponedeljak(new Date(Math.min(...vremena)));
  const kraj = new Date(
    ponedeljak(new Date(Math.max(...vremena))).getTime() + 7 * DAN_MS,
  );
  const ukupnoMs = kraj.getTime() - pocetak.getTime();

  naslovSekcije(
    doc,
    `${p.plan} · CW${kalendarskaNedelja(pocetak)}–CW${kalendarskaNedelja(
      new Date(kraj.getTime() - DAN_MS),
    )}`,
    X_PLANA,
    Y_NASLOVA_FAZA,
    DESNO - X_PLANA,
  );

  const xGrafika = X_PLANA + SIRINA_NAZIVA_PLANA;
  const sirinaGrafika = DESNO - xGrafika;
  const uX = (vreme) =>
    xGrafika + ((vreme - pocetak.getTime()) / ukupnoMs) * sirinaGrafika;

  const yVrh = Y_NASLOVA_FAZA + 4;
  // Prikazuju se sve faze; faza bez datuma ima red, ali nema traku
  const redovi = izvestaj.faze;
  const visinaReda = Math.min(5.6, 48 / redovi.length);
  const yDno = yVrh + visinaReda * redovi.length;

  // Oznake nedelja: najviše 5, da se ne preklapaju
  const ukupnoNedelja = Math.round(ukupnoMs / (7 * DAN_MS));
  const korak = Math.max(1, Math.ceil(ukupnoNedelja / 4));
  const xIzvestaja = izvestajDatum ? uX(izvestajDatum.getTime()) : null;

  doc.setDrawColor(...BOJE.linija);
  doc.setLineWidth(0.15);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...BOJE.siva);
  for (let i = 0; i < ukupnoNedelja; i += korak) {
    const vreme = pocetak.getTime() + i * 7 * DAN_MS;
    const x = uX(vreme);
    doc.line(x, yVrh, x, yDno);
    if (xIzvestaja === null || Math.abs(x - xIzvestaja) > 9) {
      doc.text(`CW${kalendarskaNedelja(new Date(vreme))}`, x, yDno + 3.5, {
        align: 'center',
      });
    }
  }

  redovi.forEach((faza, index) => {
    const y = yVrh + index * visinaReda;
    doc.setFont(FONT, 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...BOJE.tamna);
    doc.text(
      lineOgraniceno(
        doc,
        FAZE[faza.kljuc]?.[jezik] ?? faza.kljuc,
        SIRINA_NAZIVA_PLANA - 1,
        1,
      ),
      X_PLANA,
      y + visinaReda / 2 + 1,
    );

    if (!imaPlanFaze(faza)) return;

    const xOd = uX(uDatum(faza.datumOd).getTime());
    // Kraj trake je kraj poslednjeg dana faze
    const xDo = uX(uDatum(faza.datumDo).getTime() + DAN_MS);
    const sirinaFaze = Math.max(0.8, xDo - xOd);
    const visinaTrake = Math.min(2.8, visinaReda - 1);
    const yTraka = y + (visinaReda - visinaTrake) / 2;

    doc.setFillColor(...BOJE.planirano);
    doc.rect(xOd, yTraka, sirinaFaze, visinaTrake, 'F');
    const popunjeno = sirinaTrake(faza.progres, sirinaFaze);
    if (popunjeno > 0) {
      doc.setFillColor(...BOJE.narandzasta);
      doc.rect(xOd, yTraka, popunjeno, visinaTrake, 'F');
    }
  });

  if (xIzvestaja !== null) {
    doc.setDrawColor(...BOJE.tamna);
    doc.setLineWidth(0.45);
    doc.line(xIzvestaja, yVrh - 0.5, xIzvestaja, yDno);
    doc.setFont(FONT, 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...BOJE.tamna);
    doc.text(`CW${kalendarskaNedelja(izvestajDatum)}`, xIzvestaja, yDno + 3.5, {
      align: 'center',
    });
  }

  const yLegenda = yDno + 7.5;
  let xLegenda = X_PLANA;
  doc.setFont(FONT, 'normal');
  doc.setFontSize(7);
  [
    [BOJE.narandzasta, p.uradjeno],
    [BOJE.planirano, p.planirano],
  ].forEach(([boja, naziv]) => {
    doc.setFillColor(...boja);
    doc.rect(xLegenda, yLegenda - 2, 4, 2, 'F');
    doc.setTextColor(...BOJE.siva);
    doc.text(naziv, xLegenda + 5, yLegenda);
    xLegenda += 5 + doc.getTextWidth(naziv) + 4;
  });
  doc.setDrawColor(...BOJE.tamna);
  doc.setLineWidth(0.45);
  doc.line(xLegenda + 0.5, yLegenda - 2.4, xLegenda + 0.5, yLegenda + 0.4);
  doc.text(p.datumIzvestaja, xLegenda + 2, yLegenda);
};

const dodajFaze = (doc, p, jezik, izvestaj) => {
  const yNaslov = Y_NASLOVA_FAZA;
  const planFaze = fazeSaPlanom(izvestaj);
  const imaPlan = planFaze.length > 0;
  const sirinaSekcije = imaPlan ? SIRINA_FAZA_UZ_PLAN : SIRINA;
  const desnoFaza = LEVO + sirinaSekcije;
  naslovSekcije(doc, p.faze, LEVO, yNaslov, sirinaSekcije);

  const sirinaNaziva = imaPlan ? 42 : 58;
  const xTraka = LEVO + sirinaNaziva;
  const sirinaTrakeFaze = sirinaSekcije - sirinaNaziva - (imaPlan ? 11 : 16);
  let y = yNaslov + 7.5;

  izvestaj.faze.forEach((faza) => {
    const naziv = FAZE[faza.kljuc]?.[jezik] ?? faza.kljuc;
    const progres = Number(faza.progres) || 0;
    const zavrseno = progres >= 100;

    doc.setFont(FONT, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...BOJE.tamna);
    doc.text(naziv, LEVO, y);

    doc.setFillColor(...BOJE.traka);
    doc.roundedRect(xTraka, y - 2.4, sirinaTrakeFaze, 2, 1, 1, 'F');
    const popunjeno = sirinaTrake(progres, sirinaTrakeFaze);
    if (popunjeno > 0) {
      doc.setFillColor(...(zavrseno ? BOJE.tamna : BOJE.narandzasta));
      doc.roundedRect(xTraka, y - 2.4, popunjeno, 2, 1, 1, 'F');
    }

    doc.setFont(FONT, 'bold');
    doc.text(progres ? formatProcenat(progres) : '—', desnoFaza, y, {
      align: 'right',
    });
    y += 5.6;
  });

  if (imaPlan) dodajPlan(doc, p, jezik, izvestaj, planFaze);
};

const dodajTekstove = (doc, p, izvestaj, sufiks) => {
  let y = 145;
  naslovSekcije(doc, p.prethodne, LEVO, y, SIRINA);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...BOJE.tamna);
  const prethodne = lineOgraniceno(
    doc,
    izvestaj[`prethodne${sufiks}`],
    SIRINA,
    6,
  );
  doc.text(prethodne, LEVO, y + 6.5, { lineHeightFactor: 1.3 });

  y += 6.5 + 6 * VISINA_REDA_TEKSTA + 2;
  const vazno = tekst(izvestaj[`vazno${sufiks}`]);
  const sirinaVaznog = 58;
  const sirinaNarednih = vazno ? SIRINA - sirinaVaznog - 6 : SIRINA;

  naslovSekcije(doc, p.naredne, LEVO, y, sirinaNarednih);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...BOJE.tamna);
  doc.text(
    lineOgraniceno(
      doc,
      izvestaj[`naredne${sufiks}`],
      sirinaNarednih,
      vazno ? 6 : 4,
    ),
    LEVO,
    y + 6.5,
    { lineHeightFactor: 1.3 },
  );

  if (vazno) {
    const xVazno = DESNO - sirinaVaznog;
    const visinaVaznog = 32;
    doc.setFillColor(...BOJE.narandzastaSvetla);
    doc.setDrawColor(...BOJE.narandzasta);
    doc.setLineWidth(0.4);
    doc.roundedRect(xVazno, y - 3, sirinaVaznog, visinaVaznog, 1.5, 1.5, 'FD');
    doc.setFont(FONT, 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...BOJE.narandzasta);
    doc.text(p.vazno, xVazno + 3, y + 2);
    doc.setFont(FONT, 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...BOJE.tamna);
    doc.text(
      lineOgraniceno(doc, vazno, sirinaVaznog - 6, 6),
      xVazno + 3,
      y + 7,
      {
        lineHeightFactor: 1.3,
      },
    );
  }
};

const dodajFotografije = (doc, p, izvestaj, sufiks) => {
  const fotografije = izvestaj.fotografije
    .filter((foto) => foto.slika)
    .slice(0, MAKS_FOTOGRAFIJA);
  if (!fotografije.length) return;

  const yNaslov = 212;
  naslovSekcije(doc, p.fotografije, LEVO, yNaslov, SIRINA);

  const razmak = 4;
  const sirinaSlike = (SIRINA - 2 * razmak) / 3;
  const visinaSlike = 54;
  const yVrh = yNaslov + 4;

  fotografije.forEach((foto, index) => {
    const x = LEVO + index * (sirinaSlike + razmak);
    doc.setFillColor(...BOJE.pozadina);
    doc.rect(x, yVrh, sirinaSlike, visinaSlike, 'F');

    const { s, v } = uklopiSliku(foto.slika, sirinaSlike, visinaSlike);
    doc.addImage(
      foto.slika.podaci,
      'JPEG',
      x + (sirinaSlike - s) / 2,
      yVrh + (visinaSlike - v) / 2,
      s,
      v,
      `foto${index}`,
    );

    const opis = lineOgraniceno(doc, foto[`opis${sufiks}`], sirinaSlike, 1);
    doc.setFont(FONT, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...BOJE.siva);
    doc.text(opis, x, yVrh + visinaSlike + 4);
  });
};

const dodajPodnozje = (doc, p, izvestaj) => {
  const y = 289;
  doc.setDrawColor(...BOJE.linija);
  doc.setLineWidth(0.2);
  doc.line(LEVO, y - 4, DESNO, y - 4);
  doc.setFont(FONT, 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...BOJE.siva);
  doc.text(tekst(izvestaj.autor), LEVO, y);
  doc.setFont(FONT, 'bold');
  doc.setTextColor(...BOJE.tamna);
  doc.text(
    `INMOLD GROUP · ${p.izvestaj} ${tekst(
      izvestaj.sifraMasine,
    )} · CW${kalendarskaNedelja(izvestaj.datumIzvestaja)}`,
    DESNO,
    y,
    { align: 'right' },
  );
};

export const generisiIzvestajProjektaPDF = async ({ izvestaj, jezik }) => {
  const p = PREVODI[jezik];
  const sufiks = jezik === 'ENG' ? 'Eng' : 'Srb';
  const [{ regular, bold }, logoInmold] = await Promise.all([
    ucitajFontove(),
    ucitajLogo(),
  ]);

  const doc = new JsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true,
  });
  doc.addFileToVFS('Roboto-Regular.ttf', regular);
  doc.addFont('Roboto-Regular.ttf', FONT, 'normal');
  doc.addFileToVFS('Roboto-Medium.ttf', bold);
  doc.addFont('Roboto-Medium.ttf', FONT, 'bold');

  dodajZaglavlje(doc, p, izvestaj, logoInmold);
  dodajKartice(doc, p, izvestaj);
  dodajFaze(doc, p, jezik, izvestaj);
  dodajTekstove(doc, p, izvestaj, sufiks);
  dodajFotografije(doc, p, izvestaj, sufiks);
  dodajPodnozje(doc, p, izvestaj);

  return {
    doc,
    nazivFajla: `${tekst(izvestaj.sifraMasine)}_${tekst(
      izvestaj.datumIzvestaja,
    ).slice(0, 10)}_${jezik}.pdf`,
  };
};

// Slika se prevodi u JPEG na beloj pozadini: manji PDF i bez problema sa providnošću
export const pripremiSliku = (izvor, maksStrana = 1600) =>
  new Promise((resolve) => {
    if (!izvor) {
      resolve(null);
      return;
    }
    const slika = new Image();
    slika.onload = () => {
      const odnos = Math.min(
        1,
        maksStrana / Math.max(slika.naturalWidth, slika.naturalHeight),
      );
      const sirina = Math.round(slika.naturalWidth * odnos);
      const visina = Math.round(slika.naturalHeight * odnos);
      if (!sirina || !visina) {
        resolve(null);
        return;
      }
      const platno = document.createElement('canvas');
      platno.width = sirina;
      platno.height = visina;
      const ctx = platno.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, sirina, visina);
      ctx.drawImage(slika, 0, 0, sirina, visina);
      resolve({
        podaci: platno.toDataURL('image/jpeg', 0.85),
        sirina,
        visina,
      });
    };
    slika.onerror = () => resolve(null);
    slika.src = izvor;
  });

export const pripremiIzvestajIzOdgovora = async (odgovor) => {
  const [logo, fotografije] = await Promise.all([
    pripremiSliku(odgovor.logo, 600),
    Promise.all(
      (odgovor.fotografije ?? []).map(async (foto) => ({
        ...foto,
        kljucReda: `s${foto.id}`,
        slika: await pripremiSliku(foto.slika),
      })),
    ),
  ]);

  return {
    ...odgovor.izvestaj,
    faze: odgovor.faze ?? [],
    logo,
    fotografije,
  };
};
